import Keycloak from 'keycloak-js';
import { keycloakConfig, keycloakInitOptions } from '@/config/keycloak';

// User type definition (previously from @/api/auth)
// roles = Keycloak roles (admin, internal, basic)
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  clientId: string;
  roles: string[];
  partyId?: string; // Optional - party_id from database lookup (added to JWT claims in Keycloak)
  mfaEnabled?: boolean;
}

type KeycloakParsedToken = {
  sub?: string;
  email?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  azp?: string;
  aud?: string | string[];
  realm_access?: { roles?: string[] };
  /** Set by custom mapper; when absent, frontend should fetch from GET /api/users/current-user */
  party_id?: string;
};

class KeycloakService {
  private keycloak: Keycloak;
  private initialized = false;
  private initPromise: Promise<boolean> | null = null;

  constructor() {
    const globalKey = '__steerfi_keycloak_instance__';
    const store = globalThis as unknown as { [key: string]: Keycloak | undefined };
    const existing = store[globalKey];
    if (existing) {
      this.keycloak = existing;
    } else {
      const instance = new Keycloak(keycloakConfig);
      store[globalKey] = instance;
      this.keycloak = instance;
    }
  }

  async init(): Promise<boolean> {
    if (this.initialized) {
      return this.keycloak.authenticated ?? false;
    }
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = (async () => {
      try {
        // Check URL for auth callback or prompt=none redirect
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hasCode = urlParams.has('code') || hashParams.has('code');
        const hasError = urlParams.has('error') || hashParams.has('error');
        const hasPromptNone = urlParams.has('prompt') && urlParams.get('prompt') === 'none';

        // If we have an auth code or error, this is a callback - let Keycloak handle it normally
        // If we have prompt=none without code/error, we came back from a failed silent check
        // In that case, skip check-sso to prevent loop
        const shouldSkipCheckSso = hasPromptNone && !hasCode && !hasError;

        let initOptions: Record<string, unknown>;
        if (shouldSkipCheckSso) {
          // Clear the URL parameters to clean up
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
          // Initialize without check-sso to prevent another redirect
          const { ...options } = keycloakInitOptions;
          initOptions = options;
        } else {
          // Normal initialization with check-sso
          // Ensure redirectUri is set for proper callback handling
          initOptions = {
            ...keycloakInitOptions,
            redirectUri: `${window.location.origin}${window.location.pathname}`,
          };
        }

        // Add timeout to prevent hanging indefinitely
        const timeoutPromise = new Promise<boolean>((_, reject) => {
          setTimeout(() => {
            reject(new Error('Keycloak initialization timeout after 10 seconds'));
          }, 10000); // 10 second timeout
        });

        const initPromise = this.keycloak.init(initOptions);
        const authenticated = await Promise.race([initPromise, timeoutPromise]);

        this.initialized = true;
        this.setupTokenRefresh();
        return authenticated as boolean;
      } catch (error) {
        // Mark as initialized even on error so UI can proceed
        // biome-ignore lint/suspicious/noConsole: Error logging is necessary for debugging
        console.error('Keycloak initialization failed:', error);
        this.initialized = true;
        return this.keycloak.token != null;
      } finally {
        this.initPromise = null;
      }
    })();
    return this.initPromise;
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  getKeycloak(): Keycloak {
    return this.keycloak;
  }

  isAuthenticated(): boolean {
    return this.keycloak.authenticated ?? false;
  }

  getCurrentUser(): User | null {
    if (!this.isAuthenticated() || !this.keycloak.tokenParsed) return null;
    const token = this.keycloak.tokenParsed as unknown as KeycloakParsedToken;
    const roles = token.realm_access?.roles ?? [];
    const user: User = {
      id: token.sub ?? '',
      email: token.email ?? token.preferred_username ?? '',
      firstName: token.given_name ?? '',
      lastName: token.family_name ?? '',
      name: token.name ?? `${token.given_name ?? ''} ${token.family_name ?? ''}`.trim(),
      clientId: (typeof token.aud === 'string' ? token.aud : token.azp) ?? '',
      roles,
    };
    if (token.party_id) user.partyId = token.party_id;
    return user;
  }

  getToken(): string | undefined {
    return this.keycloak.token;
  }

  getUserRoles(): string[] {
    return this.keycloak.realmAccess?.roles || [];
  }

  hasRole(role: string): boolean {
    return this.keycloak.hasRealmRole(role);
  }

  async login(): Promise<void> {
    if (!this.initialized) throw new Error('Keycloak not initialized');
    // Use the full URL including pathname so callback goes to the right place
    const redirectUri = `${window.location.origin}${window.location.pathname}`;
    await this.keycloak.login({ redirectUri });
  }

  async logout(): Promise<void> {
    if (!this.initialized) throw new Error('Keycloak not initialized');
    await this.keycloak.logout({ redirectUri: window.location.origin });
  }

  async updateToken(minValidity: number = 30): Promise<boolean> {
    if (!this.initialized) return false;
    try {
      return await this.keycloak.updateToken(minValidity);
    } catch {
      return false;
    }
  }

  getAccountManagementUrl(): string {
    return this.keycloak.createAccountUrl();
  }

  private setupTokenRefresh(): void {
    setInterval(
      () => {
        this.updateToken(60).catch(() => {});
      },
      5 * 60 * 1000
    );

    this.keycloak.onTokenExpired = () => {
      this.updateToken(0).catch(() => this.login().catch(() => {}));
    };
  }
}

const keycloakService = new KeycloakService();
export default keycloakService;
export { KeycloakService };
